import { UtilsService } from "@/services/utils/utils.service";
import { authorizedAPI, unauthorizedAPI } from "@/config/axios.config";

const utils = new UtilsService();

export interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  priceInCents: number;
  paymentProcessorId?: string;
  features?: any;
  isActive: boolean;
}

export interface CreateSubscriptionDto {
  planId: string;
  companyId: string;
}

export interface Subscription {
  id: string;
  userId: string;
  planId: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  plan: SubscriptionPlan;
}

export interface Payment {
  id: string;
  subscriptionId: string;
  userId: string;
  amountInCents: number;
  currency: string;
  status: string;
  invoiceNumber?: string;
  transactionId?: string;
  paymentLinkUrl?: string;
}

export interface CreateSubscriptionResponse {
  subscription: Subscription;
  payment: Payment;
}

export interface SubscriptionDetail {
  id: string;
  userId: string;
  planId: string;
  companyId: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
  company?: {
    id: string;
    name: string;
    address?: string;
    website?: string;
    phone?: string;
  };
  plan?: SubscriptionPlan;
  payments?: Payment[];
  deployment?: {
    id: string;
    subscriptionId: string;
    ipAddress: string;
    frontendDomain: string;
    backendDomain: string;
    filesDomain?: string | null;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
}

class SubscriptionService {
  async getPlans() {
    try {
      const response = await authorizedAPI.get("/subscription-plans");
      return response.data || response;
    } catch (error) {
      try {
        const response = await unauthorizedAPI.get("/subscription-plans");
        return response.data || response;
      } catch (fallbackError) {
        throw fallbackError;
      }
    }
  }

  async getPlanById(id: string) {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/subscription-plans/${id}`)
    );
  }

  async createSubscription(data: CreateSubscriptionDto): Promise<CreateSubscriptionResponse> {
    return utils.handleApiRequest<CreateSubscriptionResponse>(() =>
      authorizedAPI.post("/subscriptions", data)
    );
  }

  async getMySubscription(): Promise<Subscription | null> {
    return utils.handleApiRequest<Subscription | null>(() =>
      authorizedAPI.get("/subscriptions/me")
    );
  }

  async upgradeSubscription(planId: string) {
    return utils.handleApiRequest(() =>
      authorizedAPI.patch("/subscriptions/upgrade", { planId })
    );
  }

  async topUpSubscription() {
    // Top up creates a renewal payment for the current subscription
    // This will create a payment for the current plan to extend the subscription
    return utils.handleApiRequest(() =>
      authorizedAPI.post("/subscriptions/top-up", {})
    );
  }

  async checkout(data: { planId?: string; subscriptionId?: string; months: number }) {
    return utils.handleApiRequest(() =>
      authorizedAPI.post("/subscriptions/checkout", data)
    );
  }

  async getSubscriptionTransition(planId: string) {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/subscriptions/transition?planId=${planId}`)
    );
  }

  // Admin methods
  async getAllSubscriptions(): Promise<SubscriptionDetail[]> {
    return utils.handleApiRequest<SubscriptionDetail[]>(() =>
      authorizedAPI.get("/subscriptions")
    );
  }

  async getSubscriptionById(id: string): Promise<SubscriptionDetail> {
    return utils.handleApiRequest<SubscriptionDetail>(() =>
      authorizedAPI.get(`/subscriptions/admin/${id}`)
    );
  }
}

export const subscriptionService = new SubscriptionService();

